import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import authFetch from '../api/authFetch';
import './DailyHighlights.css';

/* ─── helpers ─────────────────────────────────────────────────── */
const fmt = (val) => {
  if (val == null || val === 0) return '0';
  return Number(val).toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

const toYMD = (dateObj) => dateObj.toISOString().split('T')[0];

const formatDisplayDate = (date) => {
  if (!date) return '';
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const getApiBase = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1'
    ? '/api'
    : `http://${h}:5001/api`;
};

const CAT_TO_COL = { N50: 'nifty50', FO: 'fo', Others: 'cash' };

/* ─── SVG icons ───────────────────────────────────────────────── */
const TvIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const CalIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

/* ─── Symbol icon links (TV + Calendar) ──────────────────────── */
const SymLinks = ({ symbol, year, month, collection = 'nifty50' }) => {
  const calUrl = `/cs-calendar?symbol=${encodeURIComponent(symbol)}&year=${year}&month=${month}&collection=${collection}`;
  return (
    <>
      <a
        href={`https://in.tradingview.com/chart/?symbol=NSE%3A${symbol}`}
        target="_blank" rel="noopener noreferrer"
        title={`View ${symbol} on TradingView`}
        className="dh-sym-link dh-sym-tv"
        onClick={e => e.stopPropagation()}
      ><TvIcon /></a>
      <a
        href={calUrl}
        target="_blank" rel="noopener noreferrer"
        title={`CS Calendar for ${symbol}`}
        className="dh-sym-link dh-sym-cal"
        onClick={e => e.stopPropagation()}
      ><CalIcon /></a>
    </>
  );
};

/* ─── sub-components ──────────────────────────────────────────── */

/** Reusable stock row */
const StockRow = ({ symbol, csValue, direction, year, month, collection }) => (
  <div className="dh-stock-row">
    <span className="dh-stock-cell">
      <span className="dh-stock-symbol">{symbol}</span>
      <SymLinks symbol={symbol} year={year} month={month} collection={collection} />
    </span>
    <span className={`dh-badge dh-badge--${direction}`}>
      ₹{fmt(Math.abs(csValue))} L
    </span>
  </div>
);

/** Section 1 — Strategy cards */
const StrategySection = ({ strategies, ddmmyy, year, month }) => (
  <div className="dh-panel-inner">
    <div className="dh-panel-head">
      <span>📈</span> Strategy-wise Analysis <span className="dh-panel-sub">({ddmmyy})</span>
    </div>
    <div className="dh-strategy-grid">
      {Object.entries(strategies).map(([key, s]) => (
        <div className="dh-strategy-card" key={key}>
          <div className="dh-strategy-label">{s.label}</div>
          <div className={`dh-strategy-total ${s.total >= 0 ? 'positive' : 'negative'}`}>
            ₹{fmt(s.total)} L
            <span className="dh-strategy-count">({s.totalStocks} stocks)</span>
          </div>

          {s.topBuying?.length > 0 && (
            <>
              <div className="dh-sublabel">Top Buying</div>
              {s.topBuying.map((st) => (
                <StockRow key={st.symbol} symbol={st.symbol} csValue={st.csValue} direction="buy" year={year} month={month} />
              ))}
            </>
          )}
          {s.topSelling?.length > 0 && (
            <>
              <div className="dh-sublabel">Top Selling</div>
              {s.topSelling.map((st) => (
                <StockRow key={st.symbol} symbol={st.symbol} csValue={st.csValue} direction="sell" year={year} month={month} />
              ))}
            </>
          )}
          {!s.topBuying?.length && !s.topSelling?.length && (
            <div className="dh-empty">No stocks found</div>
          )}
        </div>
      ))}
    </div>
  </div>
);

/** Section 2 — Swing Reversal */
const SwingReversalSection = ({ sr }) => (
  <div className="dh-glass-card dh-card-compact">
    <h2 className="dh-section-header"><span>🔄</span> Gann Reversal</h2>
    <div className={`dh-info-banner ${sr.isReversal ? 'active' : 'inactive'}`}>
      <span className="dh-banner-icon">{sr.isReversal ? '⚡' : '🔵'}</span>
      <div>
        <strong>{sr.isReversal ? '✅ YES — Reversal Date!' : '❌ No Gann reversal today'}</strong>
        <div className="dh-banner-details">
          Sun Degree: {sr.sunDegree?.toFixed(2)}°
          {sr.matchedDegree ? ` | Matched: ${sr.matchedDegree}°` : ''}
        </div>
      </div>
    </div>
  </div>
);

/** Section 3 — Nakshatra Stocks */
const NakshatraSection = ({ nk, year, month }) => (
  <div className="dh-glass-card dh-card-flex dh-card-flex-lg">
    <h2 className="dh-section-header">
      <span>🌟</span> Nakshatra — {nk.nakshatra}
      <span className="dh-panel-sub" style={{ marginLeft: 6 }}>({nk.rashi})</span>
    </h2>
    <div className="dh-nakshatra-meta">Lord: {nk.lord} | {nk.totalStocks} stocks</div>
    <div className="dh-two-col dh-two-col-scroll">
      <div className="dh-strategy-card">
        <div className="dh-strategy-label">Top Buying</div>
        {nk.topBuying?.length > 0
          ? nk.topBuying.map((s) => <StockRow key={s.symbol} symbol={s.symbol} csValue={s.csValue} direction="buy" year={year} month={month} />)
          : <div className="dh-empty">No positive stocks</div>}
      </div>
      <div className="dh-strategy-card">
        <div className="dh-strategy-label">Top Selling</div>
        {nk.topSelling?.length > 0
          ? nk.topSelling.map((s) => <StockRow key={s.symbol} symbol={s.symbol} csValue={s.csValue} direction="sell" year={year} month={month} />)
          : <div className="dh-empty">No negative stocks</div>}
      </div>
    </div>
  </div>
);

/** Section 4 — Swing + Bhadra combined */
const SwingBhadraSection = ({ sr, bh }) => (
  <div className="dh-glass-card dh-card-compact">
    <div className="dh-sb-row">
      <div className="dh-sb-half">
        <div className="dh-sb-label"><span>🔄</span> Gann Reversal</div>
        <div className={`dh-info-banner ${sr.isReversal ? 'active' : 'inactive'}`}>
          <span className="dh-banner-icon">{sr.isReversal ? '⚡' : '🔵'}</span>
          <div>
            <strong>{sr.isReversal ? '✅ Reversal Date!' : '❌ No reversal today'}</strong>
            <div className="dh-banner-details">Sun: {sr.sunDegree?.toFixed(1)}°{sr.matchedDegree ? ` | Match: ${sr.matchedDegree}°` : ''}</div>
          </div>
        </div>
      </div>
      <div className="dh-sb-divider" />
      <div className="dh-sb-half">
        <div className="dh-sb-label"><span>🕉️</span> Time Trend Shift</div>
        <div className={`dh-info-banner ${bh.isBhadra ? 'active' : 'inactive'}`}>
          <span className="dh-banner-icon">{bh.isBhadra ? '🕉️' : '✅'}</span>
          <div>
            <strong>{bh.isBhadra ? '⚠️ Time Trend Shift Active' : '✅ No Time Trend Shift today'}</strong>
            {bh.periods?.slice(0, 1).map((p, i) => (
              <div key={i} className="dh-banner-details">
                {p.start?.split(' at ')[1] || p.start || 'N/A'} → {p.end?.split(' at ')[1] || p.end || 'N/A'}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

/** Section 5 — Top Sectors */
const SectorsSection = ({ sec, year, month }) => (
  <div className="dh-glass-card dh-card-flex dh-card-flex-lg">
    <h2 className="dh-section-header"><span>🏭</span> Top Sectors</h2>
    <div className="dh-two-col dh-two-col-scroll">
      <div className="dh-strategy-card">
        <div className="dh-strategy-label">Top Buying Sectors</div>
        {sec.topBuying?.length > 0
          ? sec.topBuying.map((s) => (
              <div key={s.sector} className="dh-stock-row">
                <span className="dh-stock-symbol">{s.sector}</span>
                <span className="dh-badge dh-badge--buy">₹{fmt(s.totalCs)} L ({s.stockCount})</span>
              </div>
            ))
          : <div className="dh-empty">No data available</div>}
      </div>
      <div className="dh-strategy-card">
        <div className="dh-strategy-label">Top Selling Sectors</div>
        {sec.topSelling?.length > 0
          ? sec.topSelling.map((s) => (
              <div key={s.sector} className="dh-stock-row">
                <span className="dh-stock-symbol">{s.sector}</span>
                <span className="dh-badge dh-badge--sell">₹{fmt(s.totalCs)} L ({s.stockCount})</span>
              </div>
            ))
          : <div className="dh-empty">No data available</div>}
      </div>
    </div>
  </div>
);

/** Combined monthly table — merges N50, FO, Others into one */
const MonthlyTable = ({ allRows, type, todayDate, year, month }) => {
  const [sortMode, setSortMode] = useState('value'); // 'date' | 'value'

  // Merge all categories, tag each row with its cat
  const merged = ['N50', 'FO', 'Others'].flatMap((cat) =>
    (allRows[cat] || []).map((r) => ({ ...r, cat }))
  );

  const sorted = [...merged].sort((a, b) => {
    if (sortMode === 'date') {
      const aToday = a.date === todayDate;
      const bToday = b.date === todayDate;
      if (aToday !== bToday) return aToday ? -1 : 1;
    }
    return type === 'buying' ? b.csValue - a.csValue : a.csValue - b.csValue;
  });

  return (
    <div className="dh-monthly-category">
      <div className="dh-monthly-header">
        <div className="dh-filter-btns">
          <button className={`dh-filter-btn${sortMode === 'value' ? ' active' : ''}`} onClick={() => setSortMode('value')}>💹 Value</button>
          <button className={`dh-filter-btn${sortMode === 'date'  ? ' active' : ''}`} onClick={() => setSortMode('date')}>📅 Date</button>
        </div>
      </div>
      <div className="dh-table-scroll">
        <table className="dh-mini-table">
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Cat</th>
              <th>Date</th>
              <th style={{ textAlign: 'right' }}>CS Value</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length > 0 ? (
              sorted.map((e, i) => {
                const isToday = e.date === todayDate;
                const rowYear  = parseInt(e.date?.split('-')[0]) || year;
                const rowMonth = parseInt(e.date?.split('-')[1]) || month;
                const collection = CAT_TO_COL[e.cat] || 'nifty50';
                return (
                  <tr key={`${e.symbol}-${e.date}-${i}`}>
                    <td>
                      <span className="dh-stock-cell">
                        <span className="dh-stock-symbol">{e.symbol}</span>
                        <SymLinks symbol={e.symbol} year={rowYear} month={rowMonth} collection={collection} />
                      </span>
                    </td>
                    <td><span className={`dh-cat-tag dh-cat-${e.cat.toLowerCase()}`}>{e.cat === 'Others' ? 'CASH' : e.cat}</span></td>
                    <td className={isToday ? 'dh-today-cell' : ''}>{e.date}</td>
                    <td className={type === 'buying' ? 'positive' : 'negative'} style={{ textAlign: 'right' }}>
                      ₹{fmt(Math.abs(e.csValue))} L
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan={4} className="dh-empty">No data available</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/** Section 6 — Highest Buying in Month */
const MonthlyBuyingSection = ({ mh, todayDate, year, month }) => (
  <div className="dh-glass-card dh-card-flex">
    <h2 className="dh-section-header">
      <span>💹</span> Highest Buying — {mh.monthName}
    </h2>
    <div className="dh-monthly-scroll">
      <MonthlyTable
        allRows={mh.highestBuying}
        type="buying"
        todayDate={todayDate}
        year={year}
        month={month}
      />
    </div>
  </div>
);

/** Section 7 — Highest Selling in Month */
const MonthlySellingSection = ({ mh, todayDate, year, month }) => (
  <div className="dh-glass-card dh-card-flex">
    <h2 className="dh-section-header">
      <span>📉</span> Highest Selling — {mh.monthName}
    </h2>
    <div className="dh-monthly-scroll">
      <MonthlyTable
        allRows={mh.highestSelling}
        type="selling"
        todayDate={todayDate}
        year={year}
        month={month}
      />
    </div>
  </div>
);

/* ─── Main page ───────────────────────────────────────────────── */
const DailyHighlights = () => {
  const [selectedDate, setSelectedDate] = useState(toYMD(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [strategyOpen, setStrategyOpen] = useState(true);

  const todayDate = toYMD(new Date());

  // Derive year & month from selectedDate for calendar/TV links
  const year  = parseInt(selectedDate.split('-')[0]);
  const month = parseInt(selectedDate.split('-')[1]);

  const loadHighlights = useCallback(async (date) => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${getApiBase()}/highlights?date=${date}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'API returned an error');
      setSelectedDate(json.date);
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load today on mount
  useEffect(() => {
    loadHighlights(selectedDate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoad = () => {
    if (!selectedDate) return;
    loadHighlights(selectedDate);
  };

  return (
    <AppLayout pageBadge="Daily Snapshot">
      <div className="dh-root">

        {/* ── Compact header bar ── */}
        <div className="dh-header-bar">
          <h1 className="dh-page-title"><span>📊</span> Daily Snapshot</h1>
          <div className="dh-date-controls">
            <input
              type="date"
              className="dh-date-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
            {data?.date && (
              <strong className="dh-data-date" aria-live="polite">
                {formatDisplayDate(data.date)}
              </strong>
            )}
            <button className="dh-btn-primary" onClick={handleLoad} disabled={loading}>
              {loading ? 'Loading…' : 'Load'}
            </button>
          </div>
        </div>

        {/* ── Status / loading overlays ── */}
        {loading && !data && (
          <div className="dh-center-overlay">
            <div className="dh-spinner" />
            <div>Loading highlights…</div>
          </div>
        )}

        {error && !loading && (
          <div className="dh-center-overlay dh-error-state">
            <strong>❌ Error Loading Data</strong>
            <div>{error}</div>
          </div>
        )}

        {!data && !loading && !error && (
          <div className="dh-center-overlay">
            Select a date and click "Load" to begin…
          </div>
        )}

        {/* ── 3-Column fixed layout ── */}
        {data && (
          <div className="dh-content-grid">

            {/* Col 1 — Strategy */}
            <div className="dh-col dh-col-strategy">
              <div className={`dh-glass-card dh-card-flex${strategyOpen ? '' : ' dh-card-collapsed'}`}>
                <button
                  type="button"
                  className="dh-section-header dh-collapse-toggle"
                  aria-expanded={strategyOpen}
                  onClick={() => setStrategyOpen((open) => !open)}
                >
                  <span>📈</span> Strategy Analysis
                  <span className="dh-panel-sub" style={{ marginLeft: 6 }}>({data.ddmmyy})</span>
                  <span className="dh-collapse-icon" aria-hidden="true">{strategyOpen ? '−' : '+'}</span>
                </button>
                {strategyOpen && (
                  <div className="dh-strategy-scroll">
                    <StrategySection strategies={data.strategies} ddmmyy={data.ddmmyy} year={year} month={month} />
                  </div>
                )}
              </div>
            </div>

            {/* Col 2 — Status cards */}
            <div className="dh-col dh-col-status">
              <SwingBhadraSection sr={data.swingReversal} bh={data.bhadraInfo} />
              <NakshatraSection nk={data.nakshatraHighlight} year={year} month={month} />
              <SectorsSection sec={data.topSectors} year={year} month={month} />
            </div>

            {/* Col 3 — Monthly tables */}
            <div className="dh-col dh-col-monthly">
              <MonthlyBuyingSection  mh={data.monthlyHighlights} todayDate={data.date || todayDate} year={year} month={month} />
              <MonthlySellingSection mh={data.monthlyHighlights} todayDate={data.date || todayDate} year={year} month={month} />
            </div>

          </div>
        )}

      </div>
    </AppLayout>
  );
};

export default DailyHighlights;
